import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Pagination,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import { docs } from './docsStyles';

const PAGE_SIZE = 60;

// Flatten the catalog `icons` field into a flat list of { family, name, svg?, dataUri? }.
// Accepts BOTH shapes:
//   - family-grouped (real catalog v2): [{ family, count, names: [...] }]
//   - flat with preview (mock / future F5):   [{ family, name, svg }] or [{ ..., dataUri }]
function flattenIcons(icons) {
  if (!Array.isArray(icons)) return [];
  return icons.flatMap(item => {
    if (Array.isArray(item.names)) {
      return item.names.map(name => ({ family: item.family, name }));
    }
    return [{ family: item.family, name: item.name, svg: item.svg, dataUri: item.dataUri }];
  });
}

// Renders the actual glyph when the catalog provides it (svg string or data URI), or a
// neutral placeholder otherwise. Real per-icon previews depend on producer feedback F5;
// until then real-catalog icons show the placeholder + name (copy still works).
function IconPreview({ icon }) {
  if (icon.dataUri) {
    return <img src={icon.dataUri} alt={icon.name} width={28} height={28} />;
  }
  if (icon.svg) {
    return (
      <img
        src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(icon.svg)}`}
        alt={icon.name}
        width={28}
        height={28}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 1,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <ImageIcon fontSize="small" color="disabled" />
    </Box>
  );
}

// Ionicons-style icon browser: search + family filter + a paginated grid (PAGE_SIZE icons per
// page) to avoid mounting thousands of DOM nodes at once. Each icon click-to-copy its usage
// snippet. Used instead of the Sandpack playground for icon-type catalog components.
function IconGallery({ component, t }) {
  const allIcons = useMemo(() => flattenIcons(component.icons), [component.icons]);
  const families = useMemo(
    () => Array.from(new Set(allIcons.map(icon => icon.family).filter(Boolean))),
    [allIcons]
  );

  const [search, setSearch] = useState('');
  const [family, setFamily] = useState(null);
  const [page, setPage] = useState(1);
  const [copied, setCopied] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allIcons.filter(icon => {
      const matchesFamily = !family || icon.family === family;
      const matchesSearch =
        !query ||
        icon.name.toLowerCase().includes(query) ||
        (icon.family || '').toLowerCase().includes(query);
      return matchesFamily && matchesSearch;
    });
  }, [allIcons, search, family]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = value => {
    setSearch(value);
    setPage(1);
  };

  const handleFamily = value => {
    setFamily(value);
    setPage(1);
  };

  const usageCode = name => `<${component.name} iconName="${name}" />`;

  const handleCopy = name => {
    const code = usageCode(name);
    if (navigator.clipboard) navigator.clipboard.writeText(code);
    setCopied(name);
  };

  if (allIcons.length === 0) {
    return (
      <Typography sx={{ fontFamily: docs.font, fontSize: 14, color: docs.textMuted }}>
        {t('internalLibraries.icons_empty', 'No icons available.')}
      </Typography>
    );
  }

  return (
    <Box sx={{ fontFamily: docs.font }}>
      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={event => handleSearch(event.target.value)}
        placeholder={t('internalLibraries.icons_search_placeholder', 'Search icons…')}
        sx={{ mb: 1 }}
      />

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        <Chip
          size="small"
          label={t('internalLibraries.all_families', 'All families')}
          color={family === null ? 'primary' : 'default'}
          variant={family === null ? 'filled' : 'outlined'}
          onClick={() => handleFamily(null)}
        />
        {families.map(fam => (
          <Chip
            key={fam}
            size="small"
            label={fam}
            color={family === fam ? 'primary' : 'default'}
            variant={family === fam ? 'filled' : 'outlined'}
            onClick={() => handleFamily(fam)}
          />
        ))}
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontFamily: docs.font, fontSize: 12, color: docs.textFaint }}>
          {t('internalLibraries.icons_count', { defaultValue: '{{count}} icons', count: filtered.length })}
          {pageCount > 1 && (
            <Box component="span" sx={{ ml: 1 }}>
              · {t('internalLibraries.icons_page', { defaultValue: 'page {{page}} of {{total}}', page, total: pageCount })}
            </Box>
          )}
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))',
          gap: 1,
        }}>
        {paginated.map(icon => (
          <Tooltip key={`${icon.family}/${icon.name}`} title={usageCode(icon.name)}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => handleCopy(icon.name)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') handleCopy(icon.name);
              }}
              sx={{
                cursor: 'pointer',
                border: '1px solid',
                borderColor: docs.border,
                borderRadius: 2,
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                textAlign: 'center',
                '&:hover': { borderColor: docs.textStrong, bgcolor: docs.bgSubtle },
              }}>
              <IconPreview icon={icon} />
              <Typography
                sx={{
                  fontFamily: docs.font,
                  fontSize: 10,
                  color: docs.textMuted,
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                {icon.name}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
            shape="rounded"
          />
        </Box>
      )}

      <Snackbar
        open={Boolean(copied)}
        autoHideDuration={1500}
        onClose={() => setCopied('')}
        message={t('internalLibraries.icons_copied', { defaultValue: 'Copied: {{code}}', code: usageCode(copied) })}
      />
    </Box>
  );
}

export default IconGallery;
